import React, { useEffect, useState, useCallback } from "react";
import Layout from "./Layout";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import axios, { cookies } from "../../axios/axios";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";
import {
  stickyActionHeaderStyle,
  stickyActionCellStyle,
} from "../../utils/stickyColumnStyles";

const RetouchingManager = () => {
  const [retouching, setRetouching] = useState([]);
  const [errorMessage, seterrorMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState(0); // 0 means no filter
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getRetouchings = useCallback(async () => {
    try {
      const response = await axios.get("/retouching/", {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          ...(statusFilter !== 0 && { status: statusFilter }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      setRetouching(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotalResults(response.data.totalResults);
    } catch (error) {
      console.log(error);
      seterrorMessage(error.response?.data?.message || "An error occurred");
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [currentPage, rowsPerPage, statusFilter, startDate, endDate]);

  useEffect(() => {
    getRetouchings();
  }, [getRetouchings]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const response = await axios.get("/retouching/", {
      params: {
        ...(statusFilter !== 0 && { status: statusFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data;
  }, [statusFilter, startDate, endDate]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const user = cookies.get("user");
  const memberId = user.user.id;

  const getStatusDetails = (status) => {
    switch (status) {
      case 1:
        return { text: "New Order", className: " bg-primary text-light" };
      case 2:
        return { text: "In Progress", className: "bg-warning text-light" };
      case 3:
        return { text: "On Hold", className: "bg-secondary text-light" };
      case 4:
        return { text: "In QC", className: "bg-info text-dark" };
      case 5:
        return { text: "Sample Sent", className: "bg-success text-light" };
      case 6:
        return { text: "In Revision", className: "bg-info text-light" };
      case 7:
        return { text: "Cancelled", className: "bg-danger text-light" };
      case 8:
        return { text: "Completed", className: "bg-success text-light" };
      default:
        return { text: "Unknown", className: "bg-dark text-light" };
    }
  };

  const csvColumns = [
    { label: "User ID", accessor: (row) => row?.userId?.id },
    { label: "Order ID", accessor: (row) => row.id },
    { label: "Order Type", accessor: (row) => row.serviceId?.service },
    { label: "Event Name", accessor: (row) => row.order_name },
    { label: "Total Image", accessor: (row) => row.quantity },
    {
      label: "Date",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      label: "Order Status",
      accessor: (row) => getStatusDetails(row.status).text,
    },
    { label: "Editor", accessor: (row) => row.editor },
    { label: "Message", accessor: (row) => row.message },
  ];

  return (
    <Layout>
      <div className="me-5">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <div className="my-4 col-md-3 mx-auto">
          <label htmlFor="statusFilter" className="form-label fw-bold">
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            className="form-select"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(Number(e.target.value))}
          >
            <option value={0}>All</option>
            <option value={1}>New Order</option>
            <option value={2}>In Progress</option>
            <option value={3}>On Hold</option>
            <option value={4}>In QC</option>
            <option value={5}>Sample Sent</option>
            <option value={6}>In Revision</option>
            <option value={7}>Cancelled</option>
            <option value={8}>Completed</option>
          </select>
        </div>
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onClear={handleClearDates}
          />
          <div className="d-flex align-items-center gap-3">
            <ExportCsvButton
              filename="retouching-orders.csv"
              columns={csvColumns}
              fetchRows={fetchAllForExport}
            />
            <RowsPerPageSelect
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </div>
        </div>
        <div
          style={{ width: "100%", overflowX: "auto" }}
          className="scrollable-table "
        >
          <table className="table table-container">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col " className="fs-14">
                  User ID
                </th>
                <th scope="col " className="fs-14">
                  Order ID
                </th>
                <th scope="col " className="fs-14">
                  Order Type
                </th>
                <th scope="col " className="fs-14">
                  Event Name
                </th>
                <th scope="col " className="fs-14">
                  Total Image
                </th>
                <th scope="col " className="fs-14">
                  Date
                </th>
                <th scope="col " className="fs-14">
                  Order Status
                </th>

                <th scope="col " className="fs-14">
                  Editor
                </th>
                <th scope="col " className="fs-14">
                  Message
                </th>
                <th scope="col " className="fs-14" style={stickyActionHeaderStyle}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {retouching
                ?.filter((val) => val?.serviceId?.service === "Retouching")
                .map((val, index) => {
                  const { text, className } = getStatusDetails(val.status);
                  const rowNumber = (currentPage - 1) * rowsPerPage + index + 1;

                  return (
                    <tr key={val.id}>
                      <th scope="row">{rowNumber}</th>
                      <td className="fs-14">{val?.userId?.id}</td>
                      <td className="fs-14">{val.id}</td>
                      <td className="fs-14">{val.serviceId?.service}</td>
                      <td className="fs-14">{val.order_name}</td>
                      <td className="fs-14">{val.quantity}</td>
                      <td className="fs-14">
                        {new Date(val.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          className={`btn text-center p-2 rounded ${className}`}
                        >
                          {text}
                        </button>
                      </td>
                      <td className="fs-14">{val.editor}</td>
                      <td className="fs-14">{val.message}</td>
                      <td className="fs-14" style={stickyActionCellStyle}>
                        <Link
                          to={`/retouching-detail-manager/${val.id}`}
                          className="d-flex align-items-center gap-2"
                        >
                          <div>
                            <FaRegEdit className="fs-5" />
                          </div>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          onPageChange={(page) =>
            setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
          }
        />
      </div>
    </Layout>
  );
};

export default RetouchingManager;
